<template lang="html">
<transition name="fade">
  <form
    class="keyboard-input-form"
    @click.self.stop="$emit('close')"
  >
    <fieldset class="keyboard-input-fieldset"
  		:class="{'invalid' : invalidInput}"
    >
      <label class="label-with-input">
        <input type="text"
    			pattern="[0-9]*"
    			ref="hours"
    			class="hours"
    			v-model="inputHours"
    			@change="onChange"
    			@paste="onChange"
          @click.stop=""
    			@keydown.up="onKeyArrowUpHours"
    			@keydown.down="onKeyArrowDownHours"
          @keydown.right="moveFocusToInput({'event': $event, 'target': 'minutes', 'direction': 'right'})"
    			@keypress.enter="onKeyEnter"
    			@keyup.esc="onEsc"
    			@keypress="onKeyHours"
    			@keyup="onKeyUpHours"
    			@blur="correctInput"
    			@focus="selectContent($event)"
    			@mouseup="onMouseUp($event)"
        >
        <span class="input-label">
          <span class="input-label-text">Hours</span>
        </span>
      </label>
      <span
        class="divider"
      >:</span>
      <label class="label-with-input">
        <input type="text"
    			pattern="[0-9]*"
    			ref="minutes"
    			class="minutes"
    			v-model="inputMinutesShown"
    			@change="onChange"
    			@paste="onChange"
          @click.stop=""
    			@keydown.up="onKeyArrowUpMinutes"
    			@keydown.down="onKeyArrowDownMinutes"
          @keydown.left="moveFocusToInput({'event': $event, 'target': 'hours', 'direction': 'left'})"
          @keydown.right="moveFocusToInput({'event': $event, 'target': 'seconds', 'direction': 'right'})"
    			@keypress.enter="onKeyEnter"
    			@keyup.esc="onEsc"
    			@keypress="onKeyMinutes"
    			@keyup="onKeyUpMinutes"
    			@blur="correctInput"
    			@focus="selectContent($event)"
    			@mouseup="onMouseUp($event)"
        >
        <span class="input-label">
          <span class="input-label-text">Minutes</span>
        </span>
      </label>
      <span
        class="divider"
      >:</span>
      <label class="label-with-input">
      	<input type="text"
    			pattern="[0-9]*"
    			ref="seconds"
          class="seconds"
    			v-model="inputSecondsShown"
    			@change="onChange"
    			@paste="onChange"
          @click.stop=""
    			@keydown.up="onKeyArrowUpSeconds"
    			@keydown.down="onKeyArrowDownSeconds"
          @keydown.left="moveFocusToInput({'event': $event, 'target': 'minutes', 'direction': 'left'})"
    			@keyup.enter="onKeyEnter"
    			@keyup.esc="onEsc"
    			@keypress="onKeySeconds"
    			@blur="correctInput"
    			@focus="selectContent($event)"
    			@mouseup="onMouseUp($event)"
        >
        <span class="input-label">
          <span class="input-label-text">Seconds</span>
        </span>
      </label>
    </fieldset>
  </form>
</transition>
</template>

<script>
import { validateDuration } from "./mixins/validateDuration";

export default {
  name: "TimerKeyboardInput",

  mixins: [validateDuration],

  props: {
    timeRemaining: {
      type: Object,
      required: true,
    },
    oldDuration: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      inputHours: (this.oldDuration / 3600) << 0,
      inputMinutes: (this.oldDuration / 60) % 60 << 0,
      inputSeconds: this.oldDuration % 60 << 0,
      inputMinutesShown: "", // String, will get leading 0 for numbers < 10
      inputSecondsShown: "", // String, will get leading 0 for numbers < 10
      newDuration: "",
      validationTimer: "",
    };
  },

  computed: {
    // For input field styling
    invalidInput() {
      const hours = this.makeAnInt(this.inputHours);
      const minutes = this.makeAnInt(this.inputMinutesShown);
      const seconds = this.makeAnInt(this.inputSecondsShown);
      return !this.validDuration({
        hours: hours,
        minutes: minutes,
        seconds: seconds,
      });
    },
  },

  mounted() {
    this.inputMinutesShown = this.addLeadingZero(this.inputMinutes);
    this.inputSecondsShown = this.addLeadingZero(this.inputSeconds);
    this.$bus.$on("TheTimer-SetFocusOnHours", (e) => {
      this.setFocusOnHours();
    });
    this.$bus.$on("TheTimer-SetFocusOnMinutes", (e) => {
      this.setFocusOnMinutes();
    });
    this.$bus.$on("TheTimer-SetFocusOnSeconds", (e) => {
      this.setFocusOnSeconds();
    });
  },

  methods: {
    // Set focus based on where the user clicked
    /////////////////////////////////////////////

    setFocusOnHours() {
      this.$nextTick(() => {
        this.$refs.hours.focus();
      });
      this.removeEventListeners();
    },
    setFocusOnMinutes() {
      this.$nextTick(() => {
        this.$refs.minutes.focus();
      });
      this.removeEventListeners();
    },
    setFocusOnSeconds() {
      this.$nextTick(() => {
        this.$refs.seconds.focus();
      });
      this.removeEventListeners();
    },
    removeEventListeners(e) {
      this.$bus.$off("TheTimer-SetFocusOnHours");
      this.$bus.$off("TheTimer-SetFocusOnMinutes");
      this.$bus.$off("TheTimer-SetFocusOnSeconds");
    },

    /////////////////////////////////////////////

    onMouseUp(e) {
      e.preventDefault(); // Focus is applied on mousedown and calls selectContent() already. This prevents the selection to get canceled again by the default browser behavior.
    },

    selectContent(e) {
      const input = e.target;
      e.preventDefault();
      input.setSelectionRange(0, input.value.length);
    },

    // Keyboard event handlers
    //////////////////////////

    onEsc(e) {
      e.preventDefault();
      this.$emit("close");
    },

    // on press enter
    onKeyEnter() {
      this.correctInput();
      this.submitToTimer();
      this.$emit("apply");
    },

    onKeyArrowUpHours(e) {
      e.preventDefault();

      this.inputHours++;
      if (this.inputMinutes > 0 || this.inputSeconds > 0) {
        if (this.inputHours > 23) this.inputHours = 23;
      } else if (this.inputHours > 24) this.inputHours = 24;
    },

    onKeyArrowDownHours(e) {
      e.preventDefault();

      this.inputHours--;
      if (this.inputHours < 0) this.inputHours = 0;
      if (
        this.inputHours === 0 &&
        this.inputMinutes === 0 &&
        this.inputSeconds === 0
      )
        this.inputHours = 1;
    },

    onKeyUpHours(e) {
      // Jump to minutes field if a second digit was entered
      // Must be done at keyUp, because otherwise the string and its length have not changed yet.
      if (
        this.inputHours.toString().length > 1 && // inputHours is longer than 1 digit
        this.acceptableAsNumberInput(e) &&
        this.acceptableDeadKeyOrAction(e) === false && // Except on backspace etc.
        this.invalidInput === false // Except when the input is not OK
      ) {
        this.$refs.minutes.select(); // Triggers the @focus listener on that input field to select its content
      }
    },

    onKeyHours(e) {
      if (this.acceptableAsNumberInput(e)) {
        this.onChange(); // Because apparrently @change is not triggered when @keypress is handled.
      }
      // Don't allow text other than numbers.
      else if (this.acceptableDeadKeyOrAction(e) === false) {
        e.preventDefault();
      }
    },

    onKeyArrowUpMinutes(e) {
      e.preventDefault();

      this.inputMinutes++;
      if (this.inputMinutes > 59) {
        this.inputMinutes = 59;
      }
      this.inputMinutesShown = this.addLeadingZero(this.inputMinutes);

      clearTimeout(this.validationTimer);
      this.validationTimer = setTimeout(() => {
        this.correctInput();
      }, 1200);
    },

    onKeyArrowDownMinutes(e) {
      e.preventDefault();

      this.inputMinutes--;
      if (this.inputMinutes < 0) this.inputMinutes = 0;
      if (
        this.inputHours === 0 &&
        this.inputMinutes === 0 &&
        this.inputSeconds === 0
      )
        this.inputMinutes = 1;

      this.inputMinutesShown = this.addLeadingZero(this.inputMinutes);
    },

    onKeyUpMinutes(e) {
      if (
        // Jump to seconds field if a second digit was entered
        // Must be done at keyUp, because otherwise the string and its length have not changed yet.
        this.inputMinutes.toString().length > 1 && // inputMinutes is longer than 1 digits
        this.acceptableAsNumberInput(e) &&
        this.acceptableDeadKeyOrAction(e) === false && // Except on backspace etc.
        this.invalidInput === false // Except when the input is not OK
      ) {
        this.$refs.seconds.select(); // Triggers the @focus listener on that input field to select its content
      }
    },

    onKeyMinutes(e) {
      if (this.acceptableAsNumberInput(e)) {
        this.onChange(); // Because apparrently @change is not triggered when @keypress is handled.
      }
      // Don't allow text other than numbers.
      else if (this.acceptableDeadKeyOrAction(e) === false) {
        e.preventDefault();
      }
    },

    onKeyArrowUpSeconds(e) {
      e.preventDefault();

      this.inputSeconds++;
      if (this.inputSeconds > 59) this.inputSeconds = 59;
      this.inputSecondsShown = this.addLeadingZero(this.inputSeconds);

      clearTimeout(this.validationTimer);
      this.validationTimer = setTimeout(() => {
        this.correctInput();
      }, 1200);
    },

    onKeyArrowDownSeconds(e) {
      e.preventDefault();

      this.inputSeconds--;
      if (this.inputSeconds < 0) this.inputSeconds = 0;
      if (
        this.inputHours === 0 &&
        this.inputMinutes === 0 &&
        this.inputSeconds === 0
      )
        this.inputSeconds = 1;

      this.inputSecondsShown = this.addLeadingZero(this.inputSeconds);
    },

    onKeySeconds(e) {
      if (this.acceptableAsNumberInput(e)) {
        this.onChange();
      } else if (this.acceptableDeadKeyOrAction(e) === false) {
        // Don't allow text other than numbers.
        e.preventDefault();
      }
    },

    moveFocusToInput(args) {
      const event = args.event;
      const input = args.event.target;
      const target = this.$refs[args.target];
      const direction = args.direction;

      if (input.selectionStart === input.selectionEnd) {
        if (direction === "right" && input.selectionEnd == input.value.length) {
          event.preventDefault();
          target.focus();
        } else if (direction === "left" && input.selectionEnd === 0) {
          event.preventDefault();
          target.focus();
        }
      }
    },

    // Keyboard event helper functions

    // Return true if input is a number or a text modification like backspace
    acceptableAsNumberInput(e) {
      let key = e.key;

      // Modern browsers support e.key
      if (key) {
        if (
          (key >= 0 && key <= 9) || // Key is a number
          key === "Backspace" ||
          key === "Delete" ||
          key === "Clear" ||
          key === "Cut" ||
          key === "Paste" ||
          key === "Copy" ||
          key === "Redo" ||
          key === "Undo"
        ) {
          return true;
        } else {
          return false;
        }
      }
      // Use e.keyCode instead
      else {
        return this.keyCodeIsForNumber(e.keyCode);
      }
    },

    // Return true if a keyboard's event's keycode is in the range for numbers
    keyCodeIsForNumber(keyCode) {
      if (
        (keyCode >= 48 && keyCode <= 57) ||
        (keyCode >= 96 && keyCode <= 105)
      ) {
        return true;
      } else {
        return false;
      }
    },

    // Return true if cursor action is allowed
    acceptableDeadKeyOrAction(e) {
      let key = e.key;

      if (key) {
        if (
          key === "Backspace" ||
          key === "Delete" ||
          key === "Clear" ||
          key === "Cut" ||
          key === "Copy" ||
          key === "Undo" ||
          key === "Tab" ||
          key === "Cancel" ||
          key === "Accept" ||
          key === "Again" ||
          key === "Escape" ||
          key === "Execute" ||
          key === "Find" ||
          key === "Alt" ||
          key === "Control" ||
          key === "Fn" ||
          key === "Meta" ||
          key === "Shift" ||
          key === "Super" ||
          key === "Enter" ||
          key === "Dead" ||
          key === "ArrowLeft" ||
          key === "ArrowRight"
        ) {
          return true;
        } else {
          return false;
        }
      }
      // Use keyCode instead
      else {
        let keyCode = e.keyCode;

        if (
          keyCode == 8 || //Backspace
          keyCode == 46 || // Delete
          keyCode == 9 || // tab
          keyCode == 27 || // esc
          keyCode == 18 || // alt
          keyCode == 17 || // ctrl
          keyCode == 16 || // shift
          keyCode == 224 || // cmd on Firefox
          keyCode == 91 || // cmd on Safari, Chrome
          keyCode == 13 || // enter
          keyCode == 37 || // arrow left
          keyCode == 39 // arrow right
        ) {
          return true;
        } else {
          return false;
        }
      }
    },

    // Validation, correction and text styling

    // If input changes, it is validated after a short bit of time
    onChange() {
      clearTimeout(this.validationTimer);
      this.validationTimer = setTimeout(() => {
        this.correctInput();
      }, 1200);
    },

    // Replaces invalid input with valid input
    correctInput() {
      this.inputHours = this.makeAnInt(this.inputHours);
      this.inputMinutes = this.makeAnInt(this.inputMinutesShown);
      this.inputSeconds = this.makeAnInt(this.inputSecondsShown);
      this.makeInputValid();
    },

    // Convert text to number
    makeAnInt(a) {
      let b = a.toString().slice(-2); // Take the last 2 digits
      b = Number.parseInt(b, 10); // Make it an int again

      if (Number.isInteger(b)) {
        // Test if it's not NaN
        return b;
      } else {
        return Number.parseInt(0);
      }
    },

    // Reduce input to a number that can be used
    makeInputValid() {
      if (this.inputHours > 23) {
        this.inputHours = 24;
        this.inputMinutes = 0;
        this.inputSeconds = 0;
      }
      if (this.inputMinutes > 59) {
        this.inputMinutes = 59;
      }
      if (this.inputSeconds > 59) {
        this.inputSeconds = 59;
      }
      this.inputMinutesShown = this.addLeadingZero(this.inputMinutes);
      this.inputSecondsShown = this.addLeadingZero(this.inputSeconds);
      this.submitToTimer();
    },

    addLeadingZero(a) {
      if (a === "") {
        return "";
      }
      if (a < 10) {
        return "0" + a;
      }
      return a;
    },

    // Now create some output for this component!

    submitToTimer() {
      this.newDuration =
        Number(this.inputHours) * 3600 +
        Number(this.inputMinutes) * 60 +
        Number(this.inputSeconds);
      this.$emit("submit", this.newDuration);
    },
  },
};
</script>

<style scoped lang="scss">
@import "../style/abstracts/variables.scss";
@import "../style/abstracts/functions.scss";
@import "../style/abstracts/mixins.scss";

// Style
//////////


.keyboard-input-form {
  // padding-top: 15%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 1fr auto 1fr;
  grid-gap: $large + rem;
  justify-items: center;
  align-items: center;

}

@media(max-width: 500px) { 
  .keyboard-input-form { 
    // position: absolute;
    // padding-top: 40%;
    padding: auto;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0%;
    display: flex;
    justify-items: center;
    align-items: center;
    justify-content: center;
    } }


.duration-type-fieldset,
.keyboard-input-fieldset {
  grid-column-start: 1;
  grid-column-end: 2;
}
.duration-type-fieldset {
  grid-row-start: 1;
  grid-row-end: 2;
  align-self: flex-end;

  // Fieldset reset
  margin: 0;
  padding: 0;
  border: 0;
  vertical-align: baseline;
}

.keyboard-input-fieldset {
  grid-row-start: 2;
  grid-row-end: 3;
  display: flex;
  align-content: center;
  padding: unquote("max(0.15em, 1.2rem)") unquote("max(0.1em, 0.8rem)");
  font-family: "BigTimer", "Jost*", "Lato", sans-serif;
  font-size: 2rem;
  font-size: unquote("max(6rem, 20vw)");
  line-height: 1;
  background: $white;
  color: $black;
  border-radius: 0.05em;
  overflow: hidden;
  box-shadow: 0 0 0.05em transparentize($color-primary, 0.5);
  transition: background-color $duration-medium + ms;

  // Responsive text sizing
  @media (min-aspect-ratio: 1336/396) {
    font-size: unquote("max(2.8rem, 60vh)");
    background: red;
  }

  // Chrome puts text higher than the other browsers
  .chrome & {
    padding-top: 0.165em;
  }
  // Edge also puts text higher than the other browsers
  .edge & {
    padding-top: 0.13em;
  }

  &.invalid {
    background: #ffe8e3;
    border-color: #ffe8e3;
    box-shadow: 0 0 0 4px $color-alert, 0 0 0.05em transparentize(#ff2d00, 0.5);
    transition: none;
  }
}
.hours,
.minutes,
.divider,
.seconds {
  height: 1em;
  margin-top: 0.03em;
}
.label-with-input {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: inherit;
  font-family: inherit;
  font-weight: inherit;
}
.hours,
.minutes,
.seconds {
  width: 1.135em;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
  font-weight: inherit;
  text-align: right; // So a possibly briefly visible third digit won't move the position of the other digits
  background: transparent;
  border: none;
  border-radius: 0; // Radius would be visible in iOS

  // Remove spinners
  appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus {
    outline: none;
  }
  &::selection {
    background-color: $color-primary-lit;
  }
}
.minutes {
  text-align: right;
}
.divider {
  width: auto;
  user-select: none;
}
.input-label {
  display: block;
  width: 1ch;
  min-width: 5rem;
  height: 0; // So it doesn't affect the positioning of time remaining
  margin-top: -0.05em;

  text-align: center;
  user-select: none;
  overflow: visible;
}
.input-label-text {
  display: block;
  font-size: $font-size-p + rem;
  font-size: unquote("max(1.2rem, 0.07em)");
  color: $color-dark;
}

.validation {
  display: block;
}

// Transitions
////////////////

keyboard-input-form.fade-enter-active {
  transition: opacity $duration-short + ms ease-out;
}
keyboard-input-form.fade-leave-active {
  transition: opacity $duration-very-short + ms ease-in;
}

keyboard-input-form.fade-enter-to,
keyboard-input-form.fade-leave {
  opacity: 1;
}

keyboard-input-form.fade-enter,
keyboard-input-form.fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/TimerKeyboardInput.vue